import { useMemo } from "react"
import styled from "styled-components"

import {
  breakpoint,
  RadioButton,
  spacing,
  Text,
  TextArea,
} from "@minuthq/meatball-ui-react"

import { ButtonRating } from "src/components/Account/BillingPortal/YourPlan/ButtonRating"
import { TCancelPlanForm } from "src/components/Account/BillingPortal/YourPlan/CancelPlanDialogWizard/useCancelPlanForm"
import { TCancelReason } from "src/data/analytics/queries/planAnalyticsQueries"
import { langKeys } from "src/i18n/langKeys"
import { TTranslateFunction, useTranslate } from "src/i18n/useTranslate"

export const CANCEL_PLAN_REASON_FORM_ID = "cancel-plan-form"

export function CancelPlanReasonStep({
  form,
  onSubmit,
}: {
  form: TCancelPlanForm
  onSubmit: () => void
}) {
  const { t, langKeys } = useTranslate()

  // Memoize cancel plan reasons since they are randomly shuffled to avoid
  // changing order between renders
  const cancelPlanReasons = useMemo(() => getCancelPlanReasons(t), [t])

  return (
    <Form id={CANCEL_PLAN_REASON_FORM_ID} onSubmit={onSubmit}>
      <Text variant="heading3">
        {t(langKeys.cancel_plan_experience_rating_question)}
      </Text>

      <ButtonRatingWrapper>
        <ButtonRating
          min={1}
          max={5}
          value={form.values.experienceRating}
          labels={{
            start: t(langKeys.cancel_plan_experience_rating_poor),
            end: t(langKeys.cancel_plan_experience_rating_excellent),
          }}
          onClick={(v) =>
            form.setValues((prev) => ({ ...prev, experienceRating: v }))
          }
          required
        />
      </ButtonRatingWrapper>

      <Text variant="heading3">{t(langKeys.cancel_plan_reason_title)}</Text>
      <Text variant="body">{t(langKeys.cancel_plan_reason_body)}</Text>

      <RadioWrapper>
        {cancelPlanReasons.map((reason) => (
          <div key={reason.reasonId}>
            <RadioButton
              name="cancelReason"
              value={reason.reasonId}
              onSelected={(_, e) => form.handleChange(e)}
              label={reason.label}
              selected={form.values.cancelReason === reason.reasonId}
              required
            />
          </div>
        ))}
      </RadioWrapper>

      <Text variant="heading3">
        {t(langKeys.cancel_plan_what_could_we_have_done_differently)}{" "}
        <Text as="span" variant="bodyS">
          ({t(langKeys.input_optional)})
        </Text>
      </Text>
      <TextArea
        name="additionalFeedback"
        value={form.values.additionalFeedback}
        onChange={(_, e) => form.handleChange(e)}
        minRows={4}
        maxRows={4}
        placeholder={t(langKeys.cancel_plan_additional_placeholder)}
      />

      <Text variant="heading3">
        {t(langKeys.cancel_plan_come_back_question)}
      </Text>
      <ButtonRatingWrapper>
        <ButtonRating
          min={1}
          max={5}
          value={form.values.returnProbability}
          labels={{
            start: t(langKeys.cancel_plan_return_probability_very_unlikely),
            end: t(langKeys.cancel_plan_return_probability_very_likely),
          }}
          onClick={(v) =>
            form.setValues((prev) => ({
              ...prev,
              returnProbability: v,
            }))
          }
          required
        />
      </ButtonRatingWrapper>
    </Form>
  )
}

// Order of array is randomized, except "other" which is always last
function getCancelPlanReasons(t: TTranslateFunction): {
  reasonId: TCancelReason
  label: string
}[] {
  const shuffledReasons = shuffleArray([
    {
      reasonId: "tech_problems" as const,
      label: t(langKeys.cancel_plan_reason_tech_problems),
    },
    {
      reasonId: "not_hosting" as const,
      label: t(langKeys.cancel_plan_reason_not_hosting),
    },
    {
      reasonId: "switching_solution" as const,
      label: t(langKeys.cancel_reason_switching_to_another_solution),
    },
    {
      reasonId: "missing_features" as const,
      label: t(langKeys.cancel_reason_missing_feature),
    },
    {
      reasonId: "not_meet_expectations" as const,
      label: t(langKeys.cancel_plan_reason_not_meet_expectations),
    },
    {
      reasonId: "too_expensive" as const,
      label: t(langKeys.cancel_reason_too_expensive),
    },
  ])

  return [
    ...shuffledReasons,
    {
      reasonId: "other" as const,
      label: `${t(langKeys.cancel_plan_reason_other)} (${t(langKeys.cancel_plan_please_specify_below)})`,
    },
  ]
}

function shuffleArray<T>(array: T[]): T[] {
  const result = array.slice() // Create a copy to avoid mutating the original
  for (let currentIndex = result.length - 1; currentIndex > 0; currentIndex--) {
    const newIndex = Math.floor(Math.random() * (currentIndex + 1))
    const [currentValue, newValue] = [result[currentIndex], result[newIndex]]
    // Swap the elements
    result[currentIndex] = newValue!
    result[newIndex] = currentValue!
  }
  return result
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing.L};
`

const ButtonRatingWrapper = styled.div`
  padding: 0;

  @media (${breakpoint.mediumUp}) {
    padding-inline: ${spacing.XL3};
  }
`

const RadioWrapper = styled.div`
  display: grid;
  gap: ${spacing.M};
`
